import { createTheme, alpha } from '@mui/material/styles';

// Fashion-forward color palette inspired by high-end fashion brands
const fashionTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1a1a1a',      // Sophisticated black
      light: '#404040',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#d4af37',      // Luxury gold accent
      light: '#e5c764',
      dark: '#b89a2f',
      contrastText: '#000000',
    },
    error: {
      main: '#c41e3a',      // Rich crimson
      light: '#ff5252',
      dark: '#8b0000',
    },
    warning: {
      main: '#ff6b6b',      // Coral pink
      light: '#ff9999',
      dark: '#cc5555',
    },
    info: {
      main: '#7c4dff',      // Electric purple
      light: '#b47cff',
      dark: '#3f1dcb',
    },
    success: {
      main: '#00c853',      // Vibrant green
      light: '#5efc82',
      dark: '#009624',
    },
    background: {
      default: '#fafafa',   // Off-white
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#666666',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: '"Playfair Display", "Didot", "Georgia", serif',
    h1: {
      fontFamily: '"Bebas Neue", "Oswald", sans-serif',
      fontSize: '4rem',
      fontWeight: 400,
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
    },
    h2: {
      fontFamily: '"Bebas Neue", "Oswald", sans-serif',
      fontSize: '3rem',
      fontWeight: 400,
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
    },
    h3: {
      fontFamily: '"Playfair Display", serif',
      fontSize: '2.5rem',
      fontWeight: 500,
      fontStyle: 'italic',
    },
    h4: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '2rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
    h5: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    h6: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    subtitle1: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '1rem',
      fontWeight: 400,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
    subtitle2: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
    body1: {
      fontFamily: '"Lato", "Helvetica Neue", sans-serif',
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: '"Lato", "Helvetica Neue", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    caption: {
      fontFamily: '"Lato", sans-serif',
      fontSize: '0.75rem',
      fontWeight: 400,
      letterSpacing: '0.03em',
    },
    overline: {
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '0.625rem',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 0, // Sharp edges for modern look
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '12px 32px',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: alpha('#1a1a1a', 0.04),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '2px',
          fontFamily: '"Montserrat", sans-serif',
          fontWeight: 500,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          fontSize: '0.7rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            '& fieldset': {
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: '#1a1a1a',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1a1a1a',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          color: '#1a1a1a',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '2px 0 24px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'rotate(10deg) scale(1.1)',
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            boxShadow: '0 8px 28px rgba(0, 0, 0, 0.25)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
        },
        elevation3: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: '2px solid #1a1a1a',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1a1a1a',
          fontSize: '0.75rem',
          fontFamily: '"Montserrat", sans-serif',
          letterSpacing: '0.03em',
          padding: '8px 16px',
          borderRadius: 0,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
        },
        bar: {
          backgroundColor: '#d4af37',
        },
      },
    },
  },
});

// Custom animations can be added as needed via sx props or styled components

export default fashionTheme;